/**
 * Creatinine Clearance — Cockcroft-Gault, BMI-adjusted (SI units).
 *
 * HSE requires the BMI-adjusted Cockcroft-Gault value (MDCalc's "second answer",
 * which takes the patient's height into account — the most accurate measure of
 * renal function). Reference: HSE/MDCalc worked example (dataset Ref 62b).
 *
 * Steps:
 *  1. Ideal Body Weight (Devine), heightInches = heightCm / 2.54
 *       Male:   IBW = 50   + 2.3 × (heightInches − 60)
 *       Female: IBW = 45.5 + 2.3 × (heightInches − 60)
 *  2. BMI = ABW / heightM²
 *  3. Weight used in Cockcroft-Gault, by BMI band:
 *       Underweight (BMI < 18.5)        → ABW (actual, no adjustment)
 *       Normal      (BMI 18.5–24.9)     → IBW, but if ABW < IBW use ABW
 *       Overweight  (BMI ≥ 25)          → AdjBW = IBW + 0.4 × (ABW − IBW)
 *  4. CrCl (mL/min) = ((140 − age) × weightUsed × k) / serumCreatinine_µmol/L
 *       k = 1.23 (male), 1.04 (female)
 *
 * Serum creatinine is expected in **µmol/L** (the Irish lab unit). The result
 * is rounded to a whole number (HSE: "whole number, no decimals").
 *
 * ⚠️ CLINICAL: verify the formula, units (µmol/L) and the sex constants against
 * the HSE Cockcroft-Gault reference before clinical use — a wrong CrCl can lead
 * to wrong dosing.
 *
 * @param {object} inputs
 * @param {number} inputs.age              - Years
 * @param {"MALE"|"FEMALE"|string} inputs.sex
 * @param {number} inputs.weightKg         - Actual body weight (ABW), kg
 * @param {number} inputs.serumCreatinine  - µmol/L
 * @param {number} inputs.heightCm         - Height, cm
 * @returns {number|null} CrCl in mL/min (whole number), or null if inputs invalid
 */
const NORMAL_BMI_LOWER_BOUND = 18.5;
const OVERWEIGHT_BMI_LOWER_BOUND = 25;
const ADJUSTED_WEIGHT_FACTOR = 0.4;
const INCHES_PER_CM = 1 / 2.54;
const DEVINE_HEIGHT_BASELINE_INCHES = 60;

const getIdealBodyWeight = ({ isMale, heightCm }) => {
  const heightInches = heightCm * INCHES_PER_CM;
  const baseWeight = isMale ? 50 : 45.5;

  return baseWeight + 2.3 * (heightInches - DEVINE_HEIGHT_BASELINE_INCHES);
};

const getWeightUsedForBmiBand = ({ actualWeight, idealWeight, bmi }) => {
  if (bmi < NORMAL_BMI_LOWER_BOUND) return actualWeight;

  if (bmi >= OVERWEIGHT_BMI_LOWER_BOUND) {
    return idealWeight + ADJUSTED_WEIGHT_FACTOR * (actualWeight - idealWeight);
  }

  return actualWeight < idealWeight ? actualWeight : idealWeight;
};

export const calculateCreatinineClearance = ({
  age,
  sex,
  weightKg,
  serumCreatinine,
  heightCm,
}) => {
  const numericAge = Number(age);
  const actualWeight = Number(weightKg);
  const numericCreatinine = Number(serumCreatinine);
  const numericHeight = Number(heightCm);

  if (
    !numericAge ||
    !actualWeight ||
    !numericCreatinine ||
    !numericHeight
  ) {
    return null;
  }

  if (numericAge >= 140) return null;

  const isMale = String(sex).toUpperCase() === "MALE";
  const sexConstant = isMale ? 1.23 : 1.04;

  let idealWeight = getIdealBodyWeight({ isMale, heightCm: numericHeight });

  // Guard against very short patients where Devine IBW goes ≤ 0.
  if (idealWeight <= 0) idealWeight = actualWeight;

  const heightM = numericHeight / 100;
  const bmi = actualWeight / (heightM * heightM);

  const weightUsed = getWeightUsedForBmiBand({
    actualWeight,
    idealWeight,
    bmi,
  });

  const creatinineClearance =
    ((140 - numericAge) * weightUsed * sexConstant) / numericCreatinine;

  if (!isFinite(creatinineClearance) || creatinineClearance <= 0) return null;

  return Math.round(creatinineClearance);
};
